"use client";

import { useEffect, useId, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { cn } from "@/lib/cn";
import { FieldFrame, fieldStateClasses, fieldSurface } from "@/components/ui/field";
import { getCities } from "@/lib/api";
import { cityRegion } from "@/lib/geo-format";
import type { CitySuggestion } from "@/lib/types";

interface CityPickerProps {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  /**
   * Optional 2-letter country code. When set, suggestions are scoped to that
   * country; when absent, the search is worldwide and picking a suggestion
   * also reports its country code.
   */
  country?: string;
  /** Current text in the field (a city name). Controlled by the parent. */
  value: string;
  /**
   * Fired on any change. `cityId`/`countryCode` are set only when a suggestion
   * is chosen from the list; typing free text (or clearing) reports `null`/
   * `undefined` — the caller then sends the string only, without a structured
   * city.
   */
  onChange: (text: string, cityId: number | null, countryCode?: string) => void;
  /** Overrides the default localized "Start typing a city…" placeholder. */
  placeholder?: string;
  containerClassName?: string;
}

const DEBOUNCE_MS = 300;
const MIN_QUERY = 2;

/**
 * City autocomplete (worldwide by default, country-scoped via the `country`
 * prop). Suggestions come from GET /cities; picking one yields a structured
 * `cityId` (needed for distance search) plus its `countryCode`, so callers can
 * autofill the country. Free text is still accepted — the caller keeps the old
 * string-only behaviour when no suggestion is chosen. Keyboard: ↑/↓ to move,
 * Enter to pick, Esc to close.
 */
export function CityPicker({
  label,
  hint,
  error,
  country,
  value,
  onChange,
  placeholder,
  containerClassName,
}: CityPickerProps) {
  const t = useTranslations("cityPicker");
  const locale = useLocale();
  const inputId = useId();
  const listId = `${inputId}-list`;
  const inputRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<CitySuggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);

  const hasError = Boolean(error);

  // Debounced lookup while the menu is open and the query is long enough.
  // Below the minimum length the menu is hidden at render time (see `showMenu`),
  // so there's no need to clear results synchronously here.
  useEffect(() => {
    const q = value.trim();
    if (!open || q.length < MIN_QUERY) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      getCities(country, q)
        .then((res) => {
          if (!cancelled) {
            setSuggestions(res);
            setActiveIndex(-1);
          }
        })
        .catch(() => {
          if (!cancelled) setSuggestions([]);
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, country, value]);

  function choose(city: CitySuggestion) {
    onChange(city.name, city.id, city.countryCode);
    setOpen(false);
    setSuggestions([]);
    setActiveIndex(-1);
  }

  function clear() {
    onChange("", null);
    setSuggestions([]);
    setActiveIndex(-1);
    inputRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(suggestions.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      if (open && activeIndex >= 0 && suggestions[activeIndex]) {
        e.preventDefault();
        choose(suggestions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  const showMenu =
    open && value.trim().length >= MIN_QUERY && suggestions.length > 0;

  return (
    <FieldFrame
      label={label}
      hint={hint}
      error={error}
      errorId={`${inputId}-error`}
      htmlFor={inputId}
      className={containerClassName}
    >
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={showMenu}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined
          }
          aria-invalid={hasError || undefined}
          aria-describedby={hasError ? `${inputId}-error` : undefined}
          autoComplete="off"
          spellCheck={false}
          placeholder={placeholder ?? t("placeholder")}
          value={value}
          onChange={(e) => {
            onChange(e.target.value, null);
            setOpen(true);
          }}
          onFocus={() => value.trim().length >= MIN_QUERY && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onKeyDown={onKeyDown}
          className={cn(
            fieldSurface,
            fieldStateClasses(hasError),
            "h-11",
            value && "pr-10",
          )}
        />

        {value && (
          <button
            type="button"
            onClick={clear}
            aria-label={t("clearCity")}
            className="absolute inset-y-0 right-2.5 flex items-center text-white/40 transition-colors hover:text-white/80"
          >
            <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        )}

        {showMenu && (
          <ul
            id={listId}
            role="listbox"
            className="absolute z-30 mt-1.5 max-h-64 w-full overflow-auto rounded-xl border border-white/12 bg-elevated/95 p-1 shadow-lift backdrop-blur-xl"
          >
            {suggestions.map((city, i) => {
              const region = cityRegion(city, locale);
              return (
                <li
                  key={city.id}
                  id={`${listId}-${i}`}
                  role="option"
                  aria-selected={i === activeIndex}
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => choose(city)}
                  className={cn(
                    "flex cursor-pointer items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm",
                    i === activeIndex ? "bg-flamingo/15 text-white" : "text-white/80",
                  )}
                >
                  <span className="truncate">
                    {city.name}
                    {region && (
                      <span className="text-xs text-white/50">{`, ${region}`}</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </FieldFrame>
  );
}
